import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import { Box, Paper, Button, Typography, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { TextInput } from '../../components/inputs';
import { PrimaryButton } from '../../components/buttons';
import { apiPost, handleApiError } from '../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../config/apiEndpoints';
import { useTranslation } from 'react-i18next';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();

  const [accountName, setAccountName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const validate = () => {
    if (!accountName.trim()) setError(t('Account name is required'));
    if (!password) setError(t('Password is required'));
    return !error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError(''); // Clear previous errors

    try {
      const response = await apiPost(
        buildApiUrl(API_ENDPOINTS.users.login),
        {
          account_name: accountName,
          password,
        },
        { skipAuth: true } // Skip authentication for login endpoint
      );

      const data = await response.json();
      const token = data.result.jwt;

      auth?.handleLogin(accountName, token);
      navigate('/dashboard');
    } catch (err: any) {
      switch (err?.errorData?.error?.additionalInfo) {
        case 'client.extended.softLockedOutLogin30Minutes':
          setError(t('Too many failed login attempts. Please try again in 30 minutes.'));
          break;

        case 'client.extended.missingResourceLogin':
          setError(t('Account name or password is incorrect. Please try again.'));
          break;

        case 'client.extended.locked':
          setError(t('Account is locked. Please contact support.'));
          break;

        default:
          handleApiError(err, t('Failed to login'));
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: 'background.lightGrey',
        padding: { xs: 1, sm: 2 },
        position: 'relative',
      }}>
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 420 },
          padding: { xs: 2, sm: 3, md: 4 },
          backgroundColor: 'background.white',
          borderRadius: 1.5,
          textAlign: 'center',
          position: 'relative',

        }}>
        <Box
          component="img"
          src="/black-logo.png"
          alt="Company Logo"
          sx={{
            display: 'block',
            mx: 'auto',
            mb: { xs: 2, sm: 3 },
            width: { xs: 100, sm: 120, md: 140 },
            height: 'auto',
            position: 'relative',
            zIndex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
          }}
        />

        <Typography
          variant="h5"
          component="h2"
          color="text.primary"
          mb={{ xs: 3, sm: 4 }}
          sx={{
            fontWeight: 600,
            color: 'text.darkGrey',
            position: 'relative',
            zIndex: 1,
            fontSize: (theme: any) => theme.typography.fontSizes.heading2,

          }}
        >
          {t('Effortless logistics')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 1 }}>
          <TextInput
            fullWidth
            id="account"
            label={t('Email')}
            value={accountName}
            onChange={(e) => { setAccountName(e.target.value); setError(''); }}
            placeholder="email@example.com"
            sx={{ mb: 2, fontSize: (theme: any) => theme.typography.fontSizes.body }}
          />

          <TextInput
            fullWidth
            id="password"
            label={t('Password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="••••••••"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{
                      color: 'text.light',
                      opacity: 0.6,
                      '&:hover': {
                        opacity: 0.8,
                        backgroundColor: 'transparent'
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: (theme: any) => theme.typography.fontSizes.heading
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 4 }}
          />

          {/* error message */}
          {error && (
            <Typography
              variant="body2"
              color="error.main"
              sx={{ mb: 2, textAlign: 'center', fontWeight: 500 }}
            >
              {error}
            </Typography>
          )}

          <PrimaryButton
            type="submit"
            fullWidth
            loading={loading}
            disabled={!!error}
          >
            {t('Login')}
          </PrimaryButton>

          <Box sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2 } }}>
            <Button
              variant="text"
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: 'text.grey',
                textTransform: 'none',
                fontSize: (theme: any) => theme.typography.fontSizes.small,
                fontWeight: 500,
                '&:hover': {
                  color: 'text.dark',
                  backgroundColor: 'transparent',
                },
              }}
            >
              {t('Forgot your password?')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
